import React, { FC } from 'react';
import cssStyles from './Typography.module.css';
import { useThemes } from '@hooks/useThemes';

export enum TypographyAppearanceVariants {
    level1 = 'display-1',
    level2 = 'display-2',
    level3 = 'display-3',
    level4 = 'display-4',
    level5 = 'display-5',
    level6 = 'display-6',
    level7 = 'display-7',
    level8 = 'display-8',
}

export enum TypographyTagVariants {
    h1 = 'h1',
    h2 = 'h2',
    h3 = 'h3',
    h4 = 'h4',
    h5 = 'h5',
    h6 = 'h6',
    p = 'p',
    span = 'span',
}

interface TypographyBreakpointsStylesProps {
    align?: 'left' | 'center' | 'right';
    fontSize?: '10' | '12' | '14' | '16' | '18' | '20' | '24' | '36' | '40' | '46' | '54';
}

export interface TypographyProps {
    id?: string;
    classes?: string;
    tagVariant?: TypographyTagVariants;
    appearanceLevel?: TypographyAppearanceVariants;
    color?: 'muted' | 'primary' | 'dark' | 'light' | 'success' | 'danger' | 'danger-dark';
    fontWeight?: '300' | '400' | '800' | '900';
    align?: TypographyBreakpointsStylesProps['align'];
    fontSize?: TypographyBreakpointsStylesProps['fontSize'];
    styles?: {
        spacedText?: boolean;
        lineThrough?: boolean;
    };
    // styles apply from breakpoint up
    breakpointsStyles?: {
        sm?: TypographyBreakpointsStylesProps;
        md?: TypographyBreakpointsStylesProps;
        lg?: TypographyBreakpointsStylesProps;
        xl?: TypographyBreakpointsStylesProps;
    };
    children?: string | JSX.Element | JSX.Element[];
}

const alignClassesMap: (align?: string, breakpoint?: string) => string = (align, breakpoint) => {
    if (!align) {
        return '';
    }

    switch (align) {
        case 'right':
            return ` ${`text${breakpoint ? '-' + breakpoint : ''}-end`}`;
        case 'center':
            return ` ${`text${breakpoint ? '-' + breakpoint : ''}-center`}`;
        default:
            return ` ${`text${breakpoint ? '-' + breakpoint : ''}-start`}`;
    }
};

const fontSizeClassesMap: (fontSize?: string, breakpoint?: string) => string = (fontSize, breakpoint) => {
    return fontSize ? ` ${cssStyles[`fs${breakpoint ? '-' + breakpoint : ''}-${fontSize}`]}` : '';
};

export const Typography: FC<TypographyProps> = ({
    id,
    classes,
    tagVariant,
    appearanceLevel,
    align,
    color,
    fontSize,
    fontWeight,
    styles,
    breakpointsStyles,
    children,
}) => {
    const { useGrayscaleTheme } = useThemes();

    const commonProps = {
        id: id,
        className:
            // appearance
            `${appearanceLevel ? cssStyles[appearanceLevel] : ''}` +
            // styles
            ` ${styles?.spacedText ? cssStyles['spaced-text-highlight'] : ''}` +
            ` ${styles?.lineThrough ? 'text-decoration-line-through' : ''}` +
            // align (can vary on different breakpoints)
            alignClassesMap(align) +
            alignClassesMap(breakpointsStyles?.sm?.align, 'sm') +
            alignClassesMap(breakpointsStyles?.md?.align, 'md') +
            alignClassesMap(breakpointsStyles?.lg?.align, 'lg') +
            alignClassesMap(breakpointsStyles?.xl?.align, 'xl') +
            // font size (can vary on different breakpoints)
            fontSizeClassesMap(fontSize) +
            fontSizeClassesMap(breakpointsStyles?.sm?.fontSize, 'sm') +
            fontSizeClassesMap(breakpointsStyles?.md?.fontSize, 'md') +
            fontSizeClassesMap(breakpointsStyles?.lg?.fontSize, 'lg') +
            fontSizeClassesMap(breakpointsStyles?.xl?.fontSize, 'xl') +
            // color
            ` ${color ? cssStyles[`text-${color}`] : ''}` +
            // font weight
            ` ${fontWeight ? cssStyles[`w${fontWeight}`] : ''}` +
            // themes
            ` ${useGrayscaleTheme ? cssStyles['theme-grayscale'] : ''}` +
            // extra classes
            ` ${classes || ''}`,
        'data-testid': 'typography',
    };

    switch (tagVariant) {
        case TypographyTagVariants.h1:
            return <h1 {...commonProps}>{children}</h1>;
        case TypographyTagVariants.h2:
            return <h2 {...commonProps}>{children}</h2>;
        case TypographyTagVariants.h3:
            return <h3 {...commonProps}>{children}</h3>;
        case TypographyTagVariants.h4:
            return <h4 {...commonProps}>{children}</h4>;
        case TypographyTagVariants.h5:
            return <h5 {...commonProps}>{children}</h5>;
        case TypographyTagVariants.h6:
            return <h6 {...commonProps}>{children}</h6>;
        case TypographyTagVariants.span:
            return <span {...commonProps}>{children}</span>;
        default:
            return <p {...commonProps}>{children}</p>;
    }
};
